import DocumentIcon from 'assets/icons/document-icon.svg?react';

import styles from './VariableDocumentsList.module.scss';
import React from 'react';
import clsx from 'clsx';
import { VariableDocumentDto } from 'services/api/api-client.types.ts';
import useElementTextOverflow, {
  OverflowDirection,
} from 'helpers/useElementTextOverflow.ts';
import { BrightTooltip } from 'components/tooltips/BrightTooltip.tsx';
import { Links } from 'application/constants/links.ts';

type VariableDocumentListItem = Omit<
  VariableDocumentDto,
  'projectId' | 'revisionId'
> & {
  projectId?: number | null;
  revisionId?: number | null;
};

type VariableDocumentListProps = {
  documents: VariableDocumentListItem[] | undefined;

  /**
   * The ID of the current revision. Only documents matching this revision will be displayed.
   */
  currentRevisionId?: number | null;

  classes?: {
    documentItem?: string;
    documentList?: string;
  };
};

export const VariableDocumentList: React.FC<VariableDocumentListProps> =
  React.memo(function VariableDocumentList(props) {
    return (
      <div className={styles.documentList}>
        {props.documents
          ?.filter(
            (doc) =>
              !props.currentRevisionId ||
              doc.revisionId === props.currentRevisionId,
          )
          .map((doc) => (
            <VariableDocumentRow {...props} key={doc.id} document={doc} />
          ))}
      </div>
    );
  });

type VariableDocumentRowProps = {
  document: VariableDocumentListItem;
  classes?: {
    documentItem?: string;
    documentList?: string;
  };
};

const VariableDocumentRow = (props: VariableDocumentRowProps) => {
  const [nameRef, isOverflowing] = useElementTextOverflow<HTMLDivElement>(
    OverflowDirection.Horizontal,
  );

  const {
    projectId,
    revisionId,
    id: documentId,
    revisionVersion,
    name: documentName,
  } = props.document;
  const linkDisabled = projectId == null || revisionId == null;

  const IconAndName = () => (
    <div className={styles.iconAndName}>
      <DocumentIcon />
      <BrightTooltip
        title={<div className={styles.nameTooltip}>{documentName}</div>}
        disableHoverListener={!isOverflowing}
        placement="top"
      >
        <div ref={nameRef} className={styles.name}>
          {documentName}
        </div>
      </BrightTooltip>
    </div>
  );

  return (
    <div
      key={documentId}
      className={clsx(styles.document, props.classes?.documentItem)}
      data-test-id={'variable-document'}
    >
      {!linkDisabled ? (
        // Can't use AppLink here because ModalContext is outside the router
        <a
          href={Links.Authorized.OpenDocument.link({
            documentId,
            projectId,
            releaseId: revisionId,
          })}
          className={styles.link}
          target={'_blank'}
        >
          <IconAndName />
        </a>
      ) : (
        <IconAndName />
      )}
      {revisionVersion && (
        <div className={styles.revision}>revision {revisionVersion}</div>
      )}
    </div>
  );
};
